import { useState, useEffect, Component } from 'react';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) return (
      <div style={{ minHeight:'100vh', background:'#0c1e09', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12, fontFamily:'monospace', color:'#ef4444', padding:24 }}>
        <div style={{ fontSize:18 }}>Report error</div>
        <pre style={{ fontSize:11, color:'#8faa8a', whiteSpace:'pre-wrap', maxWidth:600 }}>{String(this.state.error)}</pre>
      </div>
    );
    return this.props.children;
  }
}

const C = {
  green:"#16320f",greenLight:"#1f4516",greenBorder:"#3a7029",
  gold:"#d9c20d",bg:"#0c1e09",surface:"#111f0d",
  text:"#e8f0e6",textMid:"#8faa8a",textDim:"#4a6646",teal:"#34d399",red:"#ef4444",
};
const AGE_GROUPS = ["10U","12U","14U","16U","18U"];
const AGE_GROUP_COLORS = {"10U":"#34d399","12U":"#d9c20d","14U":"#60a5fa","16U":"#f97316","18U":"#a78bfa"};

function UniformReportInner({ anon = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Forward the page's own query string (?node=<id>) straight through to the API — this is what
    // lets the COPY PUBLIC LINK / COPY ANON LINK buttons in the admin modal produce a node-scoped
    // link that actually resolves to that node's report here.
    fetch('/api/uniform-report' + window.location.search)
      .then(r => r.ok ? r.json() : Promise.reject('Failed to load'))
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Uniform report not available.'); setLoading(false); });
  }, []);

  const players = data?.players || [];
  // `unavailable` is now computed server-side (shared/uniformAvailability.mjs's
  // getUnavailableForNode) and echoed as its own field, already merged with whatever node's
  // perNode additions apply — no need to recombine uniformSettings.retired/restricted here anymore.
  const unavailable = data?.unavailable || [];
  const isUnavailable = n => unavailable.includes(n);
  const base = Array.from({length: 99}, (_, i) => String(i + 1));
  const extras = unavailable.filter(n => !base.includes(n));
  const allNums = [...new Set([...extras, ...base])].sort((a, b) => { const na = Number(a), nb = Number(b); return isNaN(na)||isNaN(nb) ? a.localeCompare(b) : na - nb; });
  const cell = (jersey, ag) => players.filter(p => p.jersey === jersey && p.ageGroup === ag);
  const orange = "#f97316";
  const lastUpdated = data?.lastUpdated;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'Inter,-apple-system,sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        *{box-sizing:border-box;}
        @media print {
          .no-print{display:none!important;}
          body{background:#fff!important;color:#000!important;}
          table{border-collapse:collapse;}
          td,th{border:1px solid #ccc!important;}
        }
      `}</style>

      {/* Header */}
      <div style={{ background: C.greenLight, borderBottom: `2px solid ${C.gold}`, padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 56 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/warriors-logo.png" style={{ width: 36, height: 36, objectFit: 'contain' }} alt="Warrior Academy" onError={e => { e.target.style.display='none'; }} />
          <div>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: 4, color: C.gold, lineHeight: 1 }}>Warrior Academy</div>
            <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 2 }}>UNIFORM NUMBER REPORT{data?.nodeName ? ` — ${data.nodeName.toUpperCase()}` : ''}{anon ? ' — ANONYMOUS' : ''}</div>
          </div>
        </div>
        <button className="no-print" onClick={() => window.print()} style={{ background: C.teal, color: C.bg, border: 'none', borderRadius: 4, padding: '7px 16px', fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>🖨 PRINT</button>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
        {loading && <div style={{ textAlign: 'center', padding: '80px 0', color: C.textDim, fontSize: 13 }}>Loading…</div>}
        {error && <div style={{ textAlign: 'center', padding: '80px 0', color: C.textDim, fontSize: 14 }}>{error}</div>}

        {data && (
          <>
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ fontSize: 10, color: C.textDim, fontFamily: "'DM Mono',monospace" }}>
                {players.length} PLAYERS · SHOWING ALL 1–99
                {lastUpdated && ` · UPDATED ${new Date(lastUpdated).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}`}
              </div>
              <div style={{ display: 'flex', gap: 12, marginLeft: 'auto' }}>
                {[['ALL FREE',C.teal],['CONFLICT',orange],['ALL TAKEN',C.red],['N/A',C.textDim]].map(([lbl,col])=>(
                  <span key={lbl} style={{ fontSize: 9, color: col, fontFamily: "'DM Mono',monospace", letterSpacing: 1 }}>● {lbl}</span>
                ))}
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: C.greenLight }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', color: C.textDim, fontSize: 9, letterSpacing: 2, fontFamily: "'DM Mono',monospace", borderBottom: `2px solid ${C.gold}`, width: 70 }}>#</th>
                    {AGE_GROUPS.map(ag => (
                      <th key={ag} style={{ padding: '10px 12px', textAlign: 'center', color: AGE_GROUP_COLORS[ag], fontSize: 9, letterSpacing: 2, fontFamily: "'DM Mono',monospace", borderBottom: `2px solid ${C.gold}` }}>{ag}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allNums.map(jersey => {
                    const unavail = isUnavailable(jersey);
                    const takenAgs = AGE_GROUPS.filter(ag => cell(jersey, ag).length > 0).length;
                    const fullyAvail = !unavail && takenAgs === 0;
                    const fullyTaken = !unavail && takenAgs === AGE_GROUPS.length;
                    const rowConflict = !unavail && AGE_GROUPS.some(ag => cell(jersey, ag).length > 1);
                    const rowBg = unavail ? C.greenBorder+'18' : fullyAvail ? C.teal+'0d' : fullyTaken ? C.red+'0d' : rowConflict ? orange+'18' : 'transparent';
                    const numColor = unavail ? C.textDim : fullyAvail ? C.teal : fullyTaken ? C.red : rowConflict ? orange : C.text;
                    return (
                      <tr key={jersey} style={{ borderBottom: `1px solid ${C.greenBorder}22`, background: rowBg }}>
                        <td style={{ padding: '6px 16px', fontFamily: "'DM Mono',monospace", fontSize: 14, fontWeight: 700, color: numColor }}>#{jersey}</td>
                        {AGE_GROUPS.map(ag => {
                          if (unavail) return <td key={ag} style={{ padding: '5px 8px', textAlign: 'center' }}><span style={{ fontSize: 9, color: C.textDim, fontFamily: "'DM Mono',monospace" }}>N/A</span></td>;
                          const plist = cell(jersey, ag);
                          const conflict = plist.length > 1;
                          const taken = plist.length > 0;
                          return (
                            <td key={ag} style={{ padding: '4px 8px', textAlign: 'center' }}>
                              {taken ? (
                                <div style={{ background: conflict ? orange+'22' : C.teal+'18', border: `1px solid ${conflict ? orange+'55' : C.teal+'44'}`, borderRadius: 4, padding: '3px 8px', display: 'inline-block', minWidth: anon ? 28 : 76 }}>
                                  {anon ? (
                                    <div style={{ fontSize: conflict ? 13 : 11, color: conflict ? C.red : C.teal, fontWeight: 700, textAlign: 'center' }}>{conflict ? plist.length+'x' : 'x'}</div>
                                  ) : (
                                    <div>
                                      {plist.map((p, i) => (
                                        <div key={i} style={{ fontSize: 10, color: conflict ? orange : C.teal, fontWeight: conflict ? 700 : 500, whiteSpace: 'nowrap' }}>
                                          {String(p.name || '').split(' ').pop()}{conflict && i === 0 ? ' ⚠' : ''}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{ fontSize: 13, color: fullyAvail ? C.teal : C.teal+'33' }}>—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ textAlign: 'center', padding: '20px 0 8px', fontSize: 11, color: C.textDim }}>
              Powered by <a href="/" style={{ color: C.textDim, textDecoration: 'none' }}>Warrior Academy</a>
            </div>
          </>
          </>
        )}
      </div>
    </div>
  );
}

export default function UniformReport(props) {
  return <ErrorBoundary><UniformReportInner {...props} /></ErrorBoundary>;
}
